import React, { useRef } from 'react';

import {
  useProductImpression,
  type ImpressionParams,
} from '../hooks/useProductImpression';

/**
 * Grid-cell div that registers ITSELF for `product_impression` + dwell
 * tracking (useProductImpression), so surfaces whose cards are plain
 * `styled.div` wrappers (guides, shared boards) get ranker exposure events
 * without hand-rolling a per-card subcomponent to host the hook (hooks can't
 * run inside a `.map` body — the same constraint that produced BoardFeed's
 * FeedCard).
 *
 * Wrap it in styled-components to keep each surface's card CSS:
 *
 *   const CardWrap = styled(ImpressionCard)` ... `;
 *   <CardWrap impressionParams={buildParams(item, i)}>...</CardWrap>
 *
 * `impressionParams: undefined` renders unobserved (skeletons, stories, and
 * surfaces that opt out) — mirrors BoardFeed's getImpressionParams contract.
 */
export const ImpressionCard: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { impressionParams?: ImpressionParams }
> = ({ impressionParams, children, ...divProps }) => {
  const ref = useRef<HTMLDivElement>(null);
  useProductImpression(
    ref,
    // Placeholder is never read — the hook's effect early-returns when
    // disabled — it just satisfies the params shape (FeedCard precedent).
    impressionParams ?? { sessionId: '', productId: '' },
    impressionParams !== undefined,
  );
  return (
    <div ref={ref} {...divProps}>
      {children}
    </div>
  );
};

export default ImpressionCard;
